import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Gauge } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const empty = {
  name: "", priority: 4,
  guaranteed_kbps: 1024, max_kbps: 10240, burst_kb: 512,
  real_time: false, description: "",
};

export default function QoS() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/qos-classes"); setItems(data); }
    catch (err) { console.error("qos load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/qos-classes", form); toast.success("QoS class created"); setOpen(false); setForm(empty); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this QoS class?")) return;
    try { await api.delete(`/qos-classes/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout title="QoS Classes" subtitle="BANDWIDTH SHAPING">
      <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 mb-4 text-xs font-mono text-amber-300">
        ⚠ Control-plane only — proxy enforcement (token bucket / DSCP marking) pending.
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="qos-count">{items.length} CLASSES</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-qos-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" /> NEW CLASS</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Gauge className="w-4 h-4 text-cyan-400" /> Create QoS Class</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><L>Name</L><Input data-testid="qos-name-input" value={form.name} placeholder="voice / video / bulk" onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div><L>Priority (0 hi – 7 lo)</L><Input data-testid="qos-prio-input" type="number" min="0" max="7" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Burst (KB)</L><Input data-testid="qos-burst-input" type="number" value={form.burst_kb} onChange={(e) => setForm({ ...form, burst_kb: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Guaranteed (kbps)</L><Input data-testid="qos-gtd-input" type="number" value={form.guaranteed_kbps} onChange={(e) => setForm({ ...form, guaranteed_kbps: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Max (kbps)</L><Input data-testid="qos-max-input" type="number" value={form.max_kbps} onChange={(e) => setForm({ ...form, max_kbps: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="col-span-2 flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                <span className="text-sm">Real-time (low latency)</span>
                <Switch data-testid="qos-rt-switch" checked={form.real_time} onCheckedChange={(v) => setForm({ ...form, real_time: v })} />
              </div>
              <div className="col-span-2"><L>Description</L><Input data-testid="qos-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-qos-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="qos-list">
        {items.map((c) => (
          <div key={c.class_id} data-testid={`qos-card-${c.class_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-base font-bold tracking-tight">{c.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{c.class_id}</div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-cyan-500/40 text-cyan-400 bg-cyan-500/10 rounded-sm">prio {c.priority}{c.real_time ? " · RT" : ""}</span>
            </div>
            <div className="font-mono text-xs space-y-1 text-zinc-300 mt-2">
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">GUARANTEED</span><span className="text-cyan-400">{c.guaranteed_kbps}</span> kbps</div>
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">MAX</span><span className="text-amber-400">{c.max_kbps}</span> kbps</div>
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">BURST</span>{c.burst_kb} KB</div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
              <span className="text-[10px] font-mono text-zinc-500 whitespace-normal break-words">{c.description}</span>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-qos-${c.class_id}`} onClick={() => del(c.class_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no QoS classes yet.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
